import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Avatar, Box, Flex, HStack, Text, VStack } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { AvatarGroup } from '../../../design-system/avatar/components/avatar-group';
import type { BizyPeopleBrowserEntry, BizyPeopleSource } from '../../../lib/bizy/types';
import { Bizy } from '../../bizy';
import { bizyGreetingFallbackName, useBizyGreetingDisplayName } from '../hooks/use-bizy-greeting-display-name';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';

// New-segment greeter callout (BIZY-638). Rendered at the bottom of the chat
// timeline when a fresh segment is about to start and the member has unread
// People Browser moments. Summarizes who's worth recognizing and links into the
// People Browser at the first unread teammate. Dismissal on first message send
// is owned by the chat view (via `useBizyGreeterCallout`).

const SOURCE_LABELS: Record<BizyPeopleSource, string> = {
	bonusly: 'Bonusly',
	slack: 'Slack',
	github: 'GitHub',
	ms_teams: 'Microsoft Teams',
};

const AVATAR_STACK_MAX = 2;

function firstNameOf(entry: BizyPeopleBrowserEntry): string {
	return entry.person.name?.trim().split(/\s+/)[0] ?? '';
}

/** Human-friendly source list, e.g. "Slack, GitHub, & Bonusly". */
function formatSources(entries: BizyPeopleBrowserEntry[]): string {
	const labels = [
		...new Set(
			entries
				.map((e) => e.source)
				.filter((s): s is BizyPeopleSource => s != null)
				.map((s) => SOURCE_LABELS[s])
		),
	];
	if (labels.length <= 1) return labels[0] ?? '';
	if (labels.length === 2) return `${labels[0]} & ${labels[1]}`;
	return `${labels.slice(0, -1).join(', ')}, & ${labels[labels.length - 1]}`;
}

export interface BizyGreeterCalloutProps {
	/** Unread People Browser entries (already filtered to unread). */
	unreadEntries: BizyPeopleBrowserEntry[];
	/** Time-of-day i18n key ("greeter.morning" | "greeter.afternoon" | "greeter.evening"). */
	timeOfDayKey: string;
	fontSize?: string | object;
}

export function BizyGreeterCallout({ unreadEntries, timeOfDayKey, fontSize }: BizyGreeterCalloutProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const user = useCurrentUser();
	const viewPerson = useBizySidePanelStore((state) => state.viewPerson);
	// Nickname-preferring greeting name, matching the fresh-start greeting and
	// Bizy Desktop: the Bizy personalization nickname when set, else the shared
	// surface fallback. Hook runs before the early return below to satisfy the
	// rules of hooks.
	const greetingName = useBizyGreetingDisplayName(bizyGreetingFallbackName(user));

	const unreadCount = unreadEntries.length;
	const firstUnread = unreadEntries[0];

	// The callout is about *people*, so dedupe repeat moments by person. Names,
	// the "N people" count, and the avatar stack all derive from this unique set.
	// The backend already returns one entry per person; this stays as a guard.
	const uniquePeople: BizyPeopleBrowserEntry[] = [];
	const seenPersonIds = new Set<string>();
	for (const entry of unreadEntries) {
		if (seenPersonIds.has(entry.person.id)) continue;
		seenPersonIds.add(entry.person.id);
		uniquePeople.push(entry);
	}
	const peopleCount = uniquePeople.length;

	const hasTrackedShownRef = useRef(false);
	useEffect(() => {
		if (unreadCount === 0 || hasTrackedShownRef.current) return;
		hasTrackedShownRef.current = true;
		track(AnalyticsEvent.Bizy.GreeterShown, { unreadCount });
	}, [unreadCount, track]);

	if (unreadCount === 0 || !firstUnread) return null;

	const greetingLine = t('greeter.greetingLine', { greeting: t(timeOfDayKey), name: greetingName });

	const leadNames = uniquePeople.slice(0, AVATAR_STACK_MAX).map(firstNameOf).filter(Boolean);
	const remaining = peopleCount - leadNames.length;
	const namesSummary = remaining > 0 ? t('greeter.namesAndOthers', { names: leadNames.join(', '), count: remaining }) : leadNames.join(', ');
	const sources = formatSources(unreadEntries);

	const handleCta = () => {
		track(AnalyticsEvent.Bizy.GreeterCtaClicked, { unreadCount });
		// Open the first unseen person; the person view marks the moment seen on view.
		viewPerson({ id: firstUnread.person.id, name: firstUnread.person.name ?? '', observedMomentId: firstUnread.observedMomentId });
	};

	return (
		<Flex direction="column" align="center" gap={3} py={4} maxW="440px" w="100%" mx="auto" data-component="bizy-greeter-callout">
			<Bizy state="waving" size="md" />

			<Text fontSize="md" fontWeight="semibold" color="text.default" textAlign="center">
				{greetingLine}
			</Text>

			<Text fontSize={fontSize ?? 'sm'} color="text.soft" textAlign="center" maxW="320px" lineHeight="tall">
				<Trans
					t={t}
					i18nKey="greeter.intro"
					count={peopleCount}
					values={{ count: peopleCount }}
					components={[
						<Box
							key="cta"
							as="button"
							type="button"
							color="text.interactive"
							fontWeight="medium"
							onClick={handleCta}
							aria-label={t('greeter.ctaAriaLabel')}
							sx={{ display: 'inline', textAlign: 'left', _hover: { textDecoration: 'underline' } }}
						/>,
					]}
				/>
			</Text>

			<Box
				as="button"
				type="button"
				onClick={handleCta}
				aria-label={t('greeter.ctaAriaLabel')}
				borderWidth="1px"
				borderColor="border.soft"
				bg="bg.surface"
				borderRadius="md"
				px={3}
				py={2}
				w="100%"
				transitionProperty="common"
				transitionDuration="normal"
				_hover={{ borderColor: 'border.emphasis', bg: 'bg.muted' }}
			>
				<HStack spacing={3} align="center">
					<AvatarGroup max={AVATAR_STACK_MAX} size="sm">
						{uniquePeople.map((entry) => (
							<Avatar key={entry.person.id} name={entry.person.name ?? undefined} src={entry.person.avatarUrl ?? undefined} size="sm" />
						))}
					</AvatarGroup>
					<VStack align="stretch" spacing={0} minW={0} flex={1}>
						<Text fontSize="sm" fontWeight="semibold" color="text.default" noOfLines={1} textAlign="left">
							{namesSummary}
						</Text>
						{sources && (
							<Text fontSize="xs" color="text.muted" noOfLines={1} textAlign="left">
								{t('greeter.spottedAcross', { sources })}
							</Text>
						)}
					</VStack>
				</HStack>
			</Box>
		</Flex>
	);
}
